import { useEffect, useMemo, useState } from 'react'

/**
 * Shared click-to-edit focus behavior for token inspectors. Filters the
 * requested paths to the ones this inspector renders, calls `reveal(paths)`
 * so the caller can open the owning section, then — once the field exists in
 * the DOM — scrolls it into view and moves keyboard focus into its control.
 *
 * @param {{ paths: string[], nonce: number } | null} focusRequest
 * @param {Set<string>} renderedPaths  token paths this inspector renders
 * @param {(paths: string[]) => void} reveal  make those fields' sections visible
 * @param {{ current: Element | null }} rootRef  the inspector root
 * @returns {Set<string>} the paths to render highlighted
 */
export default function useInspectFocus(focusRequest, renderedPaths, reveal, rootRef) {
  const [pendingFocus, setPendingFocus] = useState(null)

  const focusPaths = useMemo(() => {
    if (!focusRequest?.paths) return []
    return focusRequest.paths.filter((path) => renderedPaths.has(path))
  }, [focusRequest, renderedPaths])

  const highlightSet = useMemo(() => new Set(focusPaths), [focusPaths])

  useEffect(() => {
    if (!focusRequest?.nonce || focusPaths.length === 0) return
    reveal(focusPaths)
    setPendingFocus({ path: focusPaths[0], nonce: focusRequest.nonce })
    // Intentionally keyed on the nonce alone: a new click is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest?.nonce])

  // The reveal state and pendingFocus land in the same batched commit, so
  // the revealed section (and its TokenField) exists by the time this runs.
  useEffect(() => {
    if (!pendingFocus) return
    const field = rootRef.current?.querySelector(
      `[data-testid="token-field-${pendingFocus.path}"]`
    )
    if (!field) {
      // Same-commit invariant violated (e.g. a section body became lazy).
      // Degrade to "no focus" instead of letting the pending request fire a
      // surprise scroll + focus-steal on some later unrelated re-render.
      setPendingFocus(null)
      return
    }
    if (typeof field.scrollIntoView === 'function') {
      field.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' })
    }
    const control = field.querySelector('input:not([type="color"]), select, textarea')
      || field.querySelector('input')
    control?.focus({ preventScroll: true })
    setPendingFocus(null)
  }, [pendingFocus, rootRef])

  return highlightSet
}
